// ctl-compare-tray: Sticky bottom tray for comparing selected units
//
// Attributes:
// - position (string): "bottom" | "right" (default "bottom")
// - max (number): Maximum units that can be compared (default 4)
// - open (boolean): Tray is visible
//
// Properties:
// - units (array): [{ id, name, price?, size?, ... }]
//
// Events:
// - ctl-remove (detail: { unitId, unit })
// - ctl-clear (detail: { units })
// - ctl-compare (detail: { units })
//
// Slots:
// - actions: Custom action buttons

import "../foundation/ctl-button.js";

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-text: var(--ctl-color-text);
    --ctl-muted: var(--ctl-color-muted);
    --ctl-border: var(--ctl-color-border);
    --ctl-surface: var(--ctl-color-surface);
    --ctl-brand: var(--ctl-color-brand);

    display: none;
    position: fixed;
    z-index: var(--ctl-z-tray, 1000);
    font-family: var(--ctl-font);
  }

  :host([open]) {
    display: block;
  }

  /* Bottom position */
  :host([position="bottom"]), :host(:not([position])) {
    bottom: 0;
    left: 0;
    right: 0;
  }

  /* Right position */
  :host([position="right"]) {
    top: 0;
    right: 0;
    bottom: 0;
    width: min(var(--ctl-space-95), 100vw);
  }

  .tray{
    background: var(--ctl-surface);
    border-top: var(--ctl-space-0-5) solid var(--ctl-border);
    box-shadow: 0 calc(-1 * var(--ctl-space-1-5)) var(--ctl-space-6) var(--ctl-color-overlay-7);
    padding: var(--ctl-space-4);
    max-width: var(--ctl-space-350);
    margin: 0 auto;
  }

  :host([position="right"]) .tray{
    border-top: none;
    border-left: var(--ctl-space-0-5) solid var(--ctl-border);
    box-shadow: calc(-1 * var(--ctl-space-1-5)) 0 var(--ctl-space-6) var(--ctl-color-overlay-7);
    height: 100%;
    overflow-y: auto;
    max-width: none;
  }

  .header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    margin-bottom: var(--ctl-space-3-5);
  }

  .title{
    font-weight: var(--ctl-font-weight-black);
    font-size: var(--ctl-font-size-lg);
    letter-spacing: var(--ctl-letter-spacing-tight);
    color: var(--ctl-text);
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
  }

  .count{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--ctl-space-6);
    height: var(--ctl-space-6);
    border-radius: var(--ctl-radius-full);
    background: color-mix(in srgb, var(--ctl-brand) var(--ctl-opacity-soft), var(--ctl-surface));
    color: var(--ctl-brand);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-black);
  }

  .clear{
    all: unset;
    cursor: pointer;
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-extrabold);
    color: var(--ctl-muted);
    padding: var(--ctl-space-1) var(--ctl-space-2);
    border-radius: var(--ctl-radius-sm);
    transition: color var(--ctl-transition-fast) ease, background var(--ctl-transition-fast) ease;
  }

  .clear:hover{
    color: var(--ctl-text);
    background: color-mix(in srgb, var(--ctl-border) var(--ctl-opacity-hover-strong), transparent);
  }

  .clear:focus-visible{
    outline: var(--ctl-space-0-5) solid var(--ctl-brand);
    outline-offset: var(--ctl-space-0-5);
  }

  .units{
    display: flex;
    gap: var(--ctl-space-2-5);
    flex-wrap: wrap;
    margin-bottom: var(--ctl-space-3-5);
  }

  :host([position="right"]) .units{
    flex-direction: column;
  }

  .unit{
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-2);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    background: color-mix(in srgb, var(--ctl-border) var(--ctl-opacity-hover-medium), var(--ctl-surface));
    border: var(--ctl-space-0-5) solid var(--ctl-border);
    border-radius: var(--ctl-radius-lg);
    font-size: var(--ctl-font-size-md);
    line-height: var(--ctl-line-height-normal);
    min-width: 0;
  }

  .unit-info{
    min-width: 0;
    flex: 1;
  }

  .unit-name{
    font-weight: var(--ctl-font-weight-black);
    color: var(--ctl-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .unit-meta{
    font-size: var(--ctl-font-size-xs);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-muted);
  }

  .remove{
    all: unset;
    cursor: pointer;
    width: var(--ctl-space-5);
    height: var(--ctl-space-5);
    border-radius: var(--ctl-radius-full);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--ctl-muted);
    opacity: var(--ctl-opacity-strong);
    transition: opacity var(--ctl-transition-fast) ease, background var(--ctl-transition-fast) ease;
    flex-shrink: 0;
  }

  .remove:hover{
    opacity: var(--ctl-opacity-default);
    background: color-mix(in srgb, var(--ctl-border) var(--ctl-opacity-disabled), transparent);
  }

  .remove:focus-visible{
    outline: var(--ctl-space-0-5) solid var(--ctl-brand);
    outline-offset: var(--ctl-space-0-5);
  }

  .remove svg{
    width: var(--ctl-space-3);
    height: var(--ctl-space-3);
    fill: currentColor;
  }

  .actions{
    display: flex;
    gap: var(--ctl-space-2-5);
    align-items: center;
    justify-content: flex-end;
  }

  .actions:empty{ display: none; }

  .empty{
    padding: var(--ctl-space-3-5);
    text-align: center;
    color: var(--ctl-muted);
    font-size: var(--ctl-font-size-sm);
    font-style: italic;
  }
</style>

<div class="tray" part="tray">
  <div class="header" part="header">
    <div class="title" part="title">
      Compare Units
      <span class="count" part="count" id="count">0</span>
    </div>
    <button class="clear" part="clear" id="clear" type="button">Clear all</button>
  </div>

  <div id="unitsContainer" class="units" part="units">
    <!-- Units will be rendered here -->
  </div>

  <div class="actions" part="actions">
    <slot name="actions"></slot>
    <ctl-button id="compareBtn" variant="primary" size="md">Compare now</ctl-button>
  </div>

  <div class="empty" part="empty" id="empty" hidden>
    Select units to compare
  </div>
</div>
`;

function esc(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export class CtlCompareTray extends HTMLElement {
  static get observedAttributes() {
    return ["position", "max", "open"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._count = this.shadowRoot.getElementById("count");
    this._clear = this.shadowRoot.getElementById("clear");
    this._unitsContainer = this.shadowRoot.getElementById("unitsContainer");
    this._compareBtn = this.shadowRoot.getElementById("compareBtn");
    this._empty = this.shadowRoot.getElementById("empty");

    this._units = [];

    this._onClear = this._onClear.bind(this);
    this._onCompare = this._onCompare.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("position")) this.setAttribute("position", "bottom");
    if (!this.hasAttribute("max")) this.setAttribute("max", "4");

    this._clear.addEventListener("click", this._onClear);
    this._compareBtn.addEventListener("click", this._onCompare);

    this._render();
  }

  disconnectedCallback() {
    this._clear.removeEventListener("click", this._onClear);
    this._compareBtn.removeEventListener("click", this._onCompare);
  }

  attributeChangedCallback() {
    this._render();
  }

  // ---- properties ----
  get units() {
    return this._units;
  }

  set units(v) {
    const normalized = Array.isArray(v) ? v.map((x) => this._normalizeUnit(x)) : [];
    
    // Enforce max limit when setting units directly
    if (normalized.length > this.max) {
      this._units = normalized.slice(0, this.max);
      // Emit max-reached event
      this.dispatchEvent(
        new CustomEvent("ctl-max-reached", {
          bubbles: true,
          composed: true,
          detail: { max: this.max }
        })
      );
    } else {
      this._units = normalized;
    }
    
    this._render();

    // Auto-show/hide tray
    if (this._units.length > 0) {
      this.setAttribute("open", "");
    } else {
      this.removeAttribute("open");
    }
  }

  get max() {
    return parseInt(this.getAttribute("max")) || 4;
  }

  set max(v) {
    this.setAttribute("max", String(v || 4));
  }

  _normalizeUnit(x) {
    const o = x && typeof x === "object" ? x : {};
    return {
      id: String(o.id || ""),
      name: String(o.name || ""),
      price: o.price != null ? String(o.price) : null,
      size: o.size != null ? String(o.size) : null,
      ...o
    };
  }

  // ---- public methods ----
  addUnit(unit) {
    const normalized = this._normalizeUnit(unit);
    if (!normalized.id) return;

    // Check if already exists
    const exists = this._units.some((u) => u.id === normalized.id);
    if (exists) return;

    // Check max limit
    if (this._units.length >= this.max) {
      this.dispatchEvent(
        new CustomEvent("ctl-max-reached", {
          bubbles: true,
          composed: true,
          detail: { max: this.max }
        })
      );
      return;
    }

    this._units.push(normalized);
    this._render();
    this.setAttribute("open", "");
  }

  removeUnit(unitId) {
    this._units = this._units.filter((u) => u.id !== unitId);
    this._render();

    if (this._units.length === 0) {
      this.removeAttribute("open");
    }
  }

  clearUnits() {
    this._units = [];
    this._render();
    this.removeAttribute("open");
  }

  // ---- render ----
  _render() {
    const units = this._units || [];
    const count = units.length;

    this._count.textContent = count;

    if (count === 0) {
      this._unitsContainer.innerHTML = "";
      this._empty.hidden = false;
      this._compareBtn.disabled = true;
      return;
    }

    this._empty.hidden = true;
    this._compareBtn.disabled = false;

    const html = units
      .map((unit) => {
        const meta = [unit.size, unit.price].filter(Boolean).join(" • ");

        return `
        <div class="unit" part="unit" data-unit-id="${esc(unit.id)}">
          <div class="unit-info">
            <div class="unit-name">${esc(unit.name || unit.id)}</div>
            ${meta ? `<div class="unit-meta">${esc(meta)}</div>` : ""}
          </div>
          <button class="remove" part="remove" type="button" aria-label="Remove ${esc(unit.name || unit.id)}" data-unit-id="${esc(unit.id)}">
            <svg viewBox="0 0 24 24">
              <path d="M18.3 5.7L12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3 1.4 1.4z"/>
            </svg>
          </button>
        </div>
      `;
      })
      .join("");

    this._unitsContainer.innerHTML = html;

    // Attach remove handlers
    const removeBtns = this._unitsContainer.querySelectorAll(".remove");
    removeBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const unitId = btn.dataset.unitId;
        const unit = units.find((u) => u.id === unitId);
        if (unit) {
          this.removeUnit(unitId);
          this.dispatchEvent(
            new CustomEvent("ctl-remove", {
              bubbles: true,
              composed: true,
              detail: { unitId, unit }
            })
          );
        }
      });
    });
  }

  // ---- events ----
  _onClear() {
    const unitsCopy = [...this._units];
    this.clearUnits();

    this.dispatchEvent(
      new CustomEvent("ctl-clear", {
        bubbles: true,
        composed: true,
        detail: { units: unitsCopy }
      })
    );
  }

  _onCompare() {
    if (this._units.length === 0) return;

    this.dispatchEvent(
      new CustomEvent("ctl-compare", {
        bubbles: true,
        composed: true,
        detail: { units: this._units }
      })
    );
  }
}

customElements.define("ctl-compare-tray", CtlCompareTray);
